/**
 * Run-trace furniture — the shared render pieces of ONE captured AI call (§8 / ADR-018).
 *
 * Extracted out of the `/runs` surface because a captured step is no longer read in exactly
 * one place: any surface that holds a `RunStepView` (the trace, a process-graph node, a rerun
 * readout) must render the engine chip, the token bar, the params grid and the prompt blocks
 * IDENTICALLY — the same markup, the same German vocabulary, the same honest "no params"
 * fallback. A second copy of these would drift, and a trace that renders differently in two
 * places is a trace nobody can compare.
 *
 * Pure lit-html helpers, no VM and no `this`: the caller resolves copy through its own `t()`
 * and hands the step in. The client view types live here too — they are what these functions
 * consume, so a consumer needs no import from any surface.
 */

import { html, nothing, type TemplateResult } from "@platform/web-kit/render"
import type { CareerI18n, CareerKey } from "../../i18n"

// ─── Client view types (parsed from the untrusted wire record) ──────────────────

export type RunEngine = "synthetic" | "noop" | "openrouter" | "codex" | "claude"

export interface RunPromptMsg {
  role: string
  content: string
}

export interface RunParams {
  temperature?: number
  topP?: number
  maxTokens?: number
  reasoningEffort?: string
  jsonSchemaName?: string
  jsonSchemaStrict?: boolean
}

export interface RunStepView {
  model: string
  engine: RunEngine
  promptTokens?: number
  completionTokens?: number
  cachedTokens?: number
  reasoningTokens?: number
  /** The RAW provider price. Absent unless the caller may see provider cost (D5). */
  costUsd?: number
  latencyMs?: number
  cacheHit?: boolean
  label?: string
  purpose?: string
  /** The exact prompt-artifact version (`"<seam>@<n>"`) that authored the call — the join key
   *  from a trace step to the process-graph node that owns that prompt. */
  promptRef?: string
  prompt?: RunPromptMsg[]
  promptTruncated?: boolean
  /** A human approved this call before it ran (the opt-in review gate, §9.2). */
  reviewed?: boolean
  /** …and rewrote it first, so the prompt/params below are the REVIEWER's, not the seam's. */
  reviewEdited?: boolean
  completion?: string
  completionTruncated?: boolean
  params?: RunParams
  contextNote?: string
}

// ─── Vocabulary (engine + message role; raw code token as fallback) ─────────────

export const ENGINE_KEY: Record<RunEngine, CareerKey> = {
  claude: "runs.engine.claude",
  codex: "runs.engine.codex",
  openrouter: "runs.engine.openrouter",
  synthetic: "runs.engine.synthetic",
  // The career-native deterministic backend shares the "Deterministisch" label with `synthetic`.
  noop: "runs.engine.synthetic",
}

export const ROLE_KEY: Record<string, CareerKey> = {
  system: "runs.role.system",
  user: "runs.role.user",
  assistant: "runs.role.assistant",
}

// ─── Formatting ─────────────────────────────────────────────────────────────────

/** Cached input tokens are billed at a fraction of a fresh token → de-weight them in the split. */
export const CACHE_TOKEN_WEIGHT = 0.1

export function fmtEur(usd: number, rate: number): string {
  const eur = usd * rate
  if (eur <= 0) return "€0"
  if (eur < 0.0001) return "< €0,0001"
  return `€${eur.toFixed(eur < 1 ? 4 : 2)}`.replace(".", ",")
}

export function fmtNum(n: number): string {
  return n.toLocaleString("de-DE")
}

export function fmtRate(rate: number): string {
  return rate.toFixed(2).replace(".", ",")
}

export function fmtWhen(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })
}

/**
 * Split one step's provider cost into input vs output share, de-weighting cached input tokens.
 * `null` when there is nothing to split (no price, or no tokens to attribute it to).
 */
export function costSplit(step: RunStepView): { input: number; output: number; cacheAdjusted: boolean } | null {
  const cost = step.costUsd ?? 0
  const pt = step.promptTokens ?? 0
  const ct = step.completionTokens ?? 0
  if (cost <= 0 || pt + ct <= 0) return null
  const cached = Math.min(step.cachedTokens ?? 0, pt)
  const effInput = pt - cached + cached * CACHE_TOKEN_WEIGHT
  const denom = effInput + ct
  const input = denom > 0 ? (cost * effInput) / denom : 0
  return { input, output: cost - input, cacheAdjusted: cached > 0 }
}

// ─── Render furniture ───────────────────────────────────────────────────────────

/** Which backend ran the call — German label, raw engine token when unmapped. */
export function engineChip(engine: RunEngine, t: CareerI18n["t"]): TemplateResult {
  const key = ENGINE_KEY[engine]
  return html`<span class="run-engine run-engine--${engine}" lang="de">${key ? t(key) : engine}</span>`
}

/** The fresh / cached / answer token proportions as one bar. Nothing when no tokens were counted. */
export function tokenBar(step: RunStepView): TemplateResult | typeof nothing {
  const pt = step.promptTokens ?? 0
  const ct = step.completionTokens ?? 0
  const cached = Math.min(step.cachedTokens ?? 0, pt)
  const total = pt + ct
  if (total <= 0) return nothing
  const pct = (n: number): string => `${(n / total) * 100}%`
  return html`
    <div class="run-tokenbar">
      <span class="run-tokenbar-seg run-tokenbar-seg--fresh" style=${`width:${pct(pt - cached)}`}></span>
      ${cached > 0 ? html`<span class="run-tokenbar-seg run-tokenbar-seg--cache" style=${`width:${pct(cached)}`}></span>` : nothing}
      <span class="run-tokenbar-seg run-tokenbar-seg--answer" style=${`width:${pct(ct)}`}></span>
    </div>
  `
}

/** The call params actually sent — the provider's own key names, or an honest "none" line. */
export function paramGrid(step: RunStepView, t: CareerI18n["t"]): TemplateResult {
  const p = step.params
  const rows: Array<[string, string]> = []
  if (p?.temperature != null) rows.push(["temperature", String(p.temperature)])
  if (p?.topP != null) rows.push(["top_p", String(p.topP)])
  if (p?.maxTokens != null) rows.push(["max_tokens", fmtNum(p.maxTokens)])
  if (p?.reasoningEffort) rows.push(["reasoning", p.reasoningEffort])
  if (p?.jsonSchemaName) rows.push(["json_schema", p.jsonSchemaName])
  if (p?.jsonSchemaStrict != null) rows.push(["strict", p.jsonSchemaStrict ? "true" : "false"])
  if (rows.length === 0) return html`<p class="run-params-none" lang="de">${t("runs.params.none")}</p>`
  return html`
    <div class="run-params-grid">
      ${rows.map(
        ([k, v]) => html`<div class="run-param"><span class="run-param-key">${k}</span><span class="run-param-val">${v}</span></div>`,
      )}
    </div>
  `
}

/**
 * One rendered prompt message: its role, a copy affordance (`data-act="copy-prompt"` — the host
 * must register that verb) and the verbatim content in a scrolling `<pre>`.
 */
export function promptBlock(msg: RunPromptMsg, t: CareerI18n["t"]): TemplateResult {
  const roleKey = ROLE_KEY[msg.role]
  return html`
    <div class="run-prompt-msg">
      <div class="run-prompt-head">
        <span class="run-prompt-role" lang="de">${roleKey ? t(roleKey) : msg.role}</span>
        <button type="button" data-act="copy-prompt" class="run-copy" lang="de">${t("runs.prompt.copy")}</button>
      </div>
      <pre class="run-prompt-pre">${msg.content}</pre>
    </div>
  `
}
